import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { NavigationError, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { APP_BUILD_INFO } from './generated-build-version';

type RemoteBuildInfo = {
  appVersion?: string;
  buildId?: string;
  builtAt?: string;
};

type ChunkErrorDetails = {
  message?: string;
  name?: string;
  type?: string;
  reasonMessage?: string;
};

@Injectable({ providedIn: 'root' })
export class AppVersionReloadService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  private readonly currentBuildId = APP_BUILD_INFO.buildId;
  private readonly reloadGuardKey = 'cj:build-reload-guard';
  private readonly updateCheckIntervalMs = 60_000;
  private readonly clickCheckTimeoutMs = 900;

  private initialized = false;
  private staleVersionDetected = false;
  private checkingVersion = false;
  private intervalId: number | null = null;

  init(): void {
    if (this.initialized || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.initialized = true;

    this.zone.runOutsideAngular(() => {
      this.registerBrowserListeners();
      this.startVersionPolling();
    });

    void this.checkForNewVersion();
  }

  isChunkLoadError(candidate: unknown): boolean {
    const details = this.collectErrorDetails(candidate);

    return [
      details.message,
      details.reasonMessage,
      details.name,
      details.type,
    ]
      .filter(Boolean)
      .some((value) =>
        /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
          value!,
        ),
      );
  }

  handleChunkLoadError(candidate: unknown): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.performReload('chunk-load-error');
  }

  private registerBrowserListeners(): void {
    window.addEventListener(
      'error',
      (event) => {
        if (this.isChunkLoadError(event.error ?? event.message ?? event)) {
          this.handleChunkLoadError(event.error ?? event.message ?? event);
        }
      },
      true,
    );

    window.addEventListener('unhandledrejection', (event) => {
      if (this.isChunkLoadError(event.reason)) {
        this.handleChunkLoadError(event.reason);
      }
    });

    window.addEventListener('focus', () => {
      void this.checkForNewVersion();
    });

    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'visible') {
        void this.checkForNewVersion();
      }
    });

    this.document.addEventListener(
      'pointerdown',
      (event) => {
        const anchor = (event.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null;
        if (!anchor || this.shouldIgnoreAnchorClick(event as unknown as MouseEvent, anchor)) {
          return;
        }

        void this.checkForNewVersion();
      },
      true,
    );

    this.document.addEventListener(
      'click',
      (event) => {
        const anchor = (event.target as HTMLElement | null)?.closest('a[href]') as HTMLAnchorElement | null;
        if (!anchor || this.shouldIgnoreAnchorClick(event, anchor)) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        void this.guardInternalNavigation(anchor);
      },
      true,
    );

    this.router.events
      .pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.staleVersionDetected) {
          this.performReload('stale-version-navigation');
          return;
        }

        void this.checkForNewVersion();
      });

    this.router.events
      .pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .subscribe((event) => {
        if (this.isChunkLoadError(event.error)) {
          this.handleChunkLoadError(event.error);
        }
      });
  }

  private startVersionPolling(): void {
    this.intervalId = window.setInterval(() => {
      void this.checkForNewVersion();
    }, this.updateCheckIntervalMs);
  }

  private async checkForNewVersion(): Promise<void> {
    if (this.staleVersionDetected) {
      return;
    }

    if (!isPlatformBrowser(this.platformId) || this.checkingVersion) {
      return;
    }

    this.checkingVersion = true;

    try {
      const response = await fetch(`/version.json?v=${encodeURIComponent(this.currentBuildId)}&t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        return;
      }

      const remoteBuild = (await response.json()) as RemoteBuildInfo;
      if (remoteBuild.buildId && remoteBuild.buildId !== this.currentBuildId) {
        this.staleVersionDetected = true;
      }
    } catch {
      // Silent on purpose: version checks should never disrupt navigation.
    } finally {
      this.checkingVersion = false;
    }
  }

  private async guardInternalNavigation(anchor: HTMLAnchorElement): Promise<void> {
    const targetUrl = anchor.getAttribute('href');
    if (!targetUrl) {
      return;
    }

    const isSameOrigin = anchor.origin === window.location.origin;
    const isInternalPath = targetUrl.startsWith('/') || targetUrl.startsWith('#');

    if (!isSameOrigin || !isInternalPath) {
      window.location.assign(anchor.href);
      return;
    }

    if (this.staleVersionDetected) {
      this.performReload('stale-version-click');
      return;
    }

    const stillFresh = await this.checkForNewVersionWithTimeout();
    if (!stillFresh) {
      this.performReload('stale-version-click');
      return;
    }

    this.zone.run(() => {
      void this.router.navigateByUrl(targetUrl);
    });
  }

  private shouldIgnoreAnchorClick(event: MouseEvent, anchor: HTMLAnchorElement): boolean {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return true;
    }

    const targetAttr = anchor.getAttribute('target');
    if (targetAttr && targetAttr !== '_self') {
      return true;
    }

    const href = anchor.getAttribute('href') ?? '';
    if (!href || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
      return true;
    }

    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"], button')) {
      return true;
    }

    return false;
  }

  private async checkForNewVersionWithTimeout(): Promise<boolean> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), this.clickCheckTimeoutMs);

    try {
      const response = await fetch(`/version.json?v=${encodeURIComponent(this.currentBuildId)}&t=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        return true;
      }

      const remoteBuild = (await response.json()) as RemoteBuildInfo;
      if (remoteBuild.buildId && remoteBuild.buildId !== this.currentBuildId) {
        this.staleVersionDetected = true;
        return false;
      }

      return true;
    } catch {
      return true;
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  private performReload(reason: string): void {
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    const guardValue = window.sessionStorage.getItem(this.reloadGuardKey);

    if (guardValue) {
      try {
        const parsed = JSON.parse(guardValue) as { path?: string; buildId?: string; reason?: string; at?: number };
        const samePath = parsed.path === currentPath;
        const sameBuild = parsed.buildId === this.currentBuildId;
        const stillFresh = typeof parsed.at === 'number' && Date.now() - parsed.at < 15_000;

        if (samePath && sameBuild && stillFresh) {
          return;
        }
      } catch {
        // Ignore malformed guard values.
      }
    }

    window.sessionStorage.setItem(
      this.reloadGuardKey,
      JSON.stringify({
        path: currentPath,
        buildId: this.currentBuildId,
        reason,
        at: Date.now(),
      }),
    );

    window.location.reload();
  }

  private collectErrorDetails(candidate: unknown): ChunkErrorDetails {
    if (!candidate || typeof candidate !== 'object') {
      return {
        message: typeof candidate === 'string' ? candidate : undefined,
      };
    }

    const value = candidate as {
      message?: string;
      name?: string;
      type?: string;
      reason?: { message?: string; name?: string; type?: string } | string;
    };

    return {
      message: value.message,
      name: value.name,
      type: value.type,
      reasonMessage: typeof value.reason === 'string' ? value.reason : value.reason?.message,
    };
  }
}
